import React, { createContext, useContext, useState, useCallback } from 'react';
import Icon from '../AppIcon';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: notification?.type || 'success',
      title: notification?.title,
      message: notification?.message,
      duration: notification?.duration || 5000,
    };

    setNotifications((prev) => [...prev, newNotification]);

    if (newNotification?.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification?.duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev?.filter((notification) => notification?.id !== id));
  }, []);

  const showSuccess = useCallback((title, message, duration) => {
    return addNotification({ type: 'success', title, message, duration });
  }, [addNotification]);

  const showError = useCallback((title, message, duration) => {
    return addNotification({ type: 'error', title, message, duration });
  }, [addNotification]);

  const showWarning = useCallback((title, message, duration) => {
    return addNotification({ type: 'warning', title, message, duration });
  }, [addNotification]);

  const showInfo = useCallback((title, message, duration) => {
    return addNotification({ type: 'info', title, message, duration });
  }, [addNotification]);

  return (
    <NotificationContext.Provider
      value={{
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeNotification,
      }}
    >
      {children}
      <NotificationContainer notifications={notifications} onClose={removeNotification} />
    </NotificationContext.Provider>
  );
};

const NotificationContainer = ({ notifications, onClose }) => {
  if (notifications?.length === 0) return null;

  return (
    <div className="toast-container">
      {notifications?.map((notification) => (
        <Toast
          key={notification?.id}
          notification={notification}
          onClose={() => onClose(notification?.id)}
        />
      ))}
    </div>
  );
};

const Toast = ({ notification, onClose }) => {
  const getIcon = () => {
    switch (notification?.type) {
      case 'success':
        return { name: 'CheckCircle', color: 'var(--color-success)' };
      case 'error':
        return { name: 'XCircle', color: 'var(--color-error)' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'info':
        return { name: 'Info', color: 'var(--color-primary)' };
      default:
        return { name: 'Bell', color: 'var(--color-foreground)' };
    }
  };

  const icon = getIcon();

  return (
    <div className={`toast ${notification?.type}`}>
      <div className="toast-icon">
        <Icon name={icon?.name} size={20} color={icon?.color} />
      </div>
      <div className="toast-content">
        {notification?.title && <div className="toast-title">{notification?.title}</div>}
        {notification?.message && <div className="toast-message">{notification?.message}</div>}
      </div>
      <button className="toast-close" onClick={onClose} aria-label="Close notification">
        <Icon name="X" size={18} />
      </button>
    </div>
  );
};

export default NotificationProvider;